/**
 * ============================================================================
 * ENTRY POINT
 * ============================================================================
 */

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router/index.js'
import { useAppStore } from '@/stores/useAppStore'
import accountSocket from '@/services/accountSocket'
import FloatingVue from 'floating-vue'
import { ensureBitrix24Sdk, isBitrix24Mode } from '@/utils/bitrixInternalNavigation'
import 'floating-vue/dist/style.css'

// Стили
import '../styleguide.css'
import '../globals.css'
import '@fortawesome/fontawesome-free/css/all.css'
import './assets/tailwind.css'

// В Bitrix/iframe кросс-доменные ошибки иногда приходят как "Script error."
// без стека и ломают dev overlay. Фильтруем только этот шум в development.
const isIgnorableScriptError = value => {
	const text = String(value || '').toLowerCase()
	return text.includes('script error')
}

if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
	window.addEventListener(
		'error',
		event => {
			const candidate = event?.message || event?.error?.message || ''
			if (!isIgnorableScriptError(candidate)) return
			event.preventDefault?.()
			event.stopImmediatePropagation?.()
		},
		true,
	)

	window.addEventListener(
		'unhandledrejection',
		event => {
			const reason = event?.reason
			const candidate = reason?.message || reason || ''
			if (!isIgnorableScriptError(candidate)) return
			event.preventDefault?.()
			event.stopImmediatePropagation?.()
		},
		true,
	)
}

// ============================================================================
// Инициализация приложения
// ============================================================================

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(router)
app.use(FloatingVue)

// ============================================================================
// Инициализация STORE
// ============================================================================

const appStore = useAppStore()

console.log('[App Init] Initializing app store...')

try {
	appStore.initializeFromStorage()
	console.log('[App Init] Store initialized:', {
		isAuthenticated: appStore.isAuthenticated,
		user: appStore.user,
		hasToken: !!localStorage.getItem('access_token'),
	})
} catch (error) {
	console.error('[App Init] Failed to initialize from storage:', error)
	localStorage.removeItem('access_token')
	localStorage.removeItem('refresh_token')
}

const existingToken = localStorage.getItem('access_token')
if (existingToken) {
	try {
		const payload = JSON.parse(atob(existingToken.split('.')[1]))
		const expiresAt = payload.exp * 1000
		const now = Date.now()

		const loginTime = localStorage.getItem('login_time')
		const MAX_SESSION_TIME = 8 * 60 * 60 * 1000

		if (loginTime && now - parseInt(loginTime, 10) > MAX_SESSION_TIME) {
			console.log('[App Init] Session is older than 8 hours')
			appStore.addNotification({
				type: 'warning',
				title: 'Сессия истекла',
				message: 'Ваша сессия длится более 8 часов. Рекомендуется повторно войти в систему.',
				duration: 6000,
				channel: 'toast',
			})
		}

		if (expiresAt > now) {
			accountSocket.connectWithToken(existingToken)
			console.log(
				'[App Init] Token is valid, expires in:',
				Math.round((expiresAt - now) / 1000 / 60),
				'minutes',
			)
		} else {
			console.log('[App Init] Access token expired, will refresh on first request')
		}
	} catch (error) {
		console.error('[App Init] Failed to decode token:', error)
		localStorage.removeItem('access_token')
		localStorage.removeItem('refresh_token')
		localStorage.removeItem('login_time')
	}
}

// ============================================================================
// Обработчик разлогинивания
// ============================================================================

window.addEventListener('auth:logout', (event) => {
	const reason = event.detail?.reason
	const bitrixBootstrapPending = localStorage.getItem('bitrix24_bootstrap_pending') === 'true'

	if (bitrixBootstrapPending && reason === 'token_expired') {
		return
	}

	// Отключаем сокет
	accountSocket.disconnect()
	
	// Выполняем logout в store
	appStore.logout()

	// Показываем уведомление в зависимости от причины
	if (reason === 'token_expired') {
		appStore.addNotification({
			type: 'warning',
			title: 'Сессия истекла',
			message: 'Ваша сессия истекла. Пожалуйста, войдите снова.',
			duration: 6000,
			channel: 'toast',
		})
	} else if (reason === 'account_blocked') {
		// Уведомление уже показывается в BlockedAccountModal.
	} else if (reason === 'manual') {
		// Обычный выход - не показываем уведомление
	}

	// Определяем контекст (Bitrix24 или обычный)
	const isBitrixIframe = (() => {
		try {
			return window.self !== window.top
		} catch (_) {
			return true
		}
	})()
	const isBitrixContext = localStorage.getItem('bitrix24_mode') === 'true' || isBitrixIframe

	// Редирект на страницу входа
	router.push(isBitrixContext ? '/bitrix24-auth' : '/auth')
})

app.config.errorHandler = (err, instance, info) => {
	console.error('Vue Error:', err)
	console.error('Info:', info)

	if (appStore.showError) {
		appStore.showError('Произошла ошибка. Пожалуйста, обновите страницу.')
	}
}

if (isBitrix24Mode()) {
	ensureBitrix24Sdk().then(isReady => {
		console.log('[App Init] Bitrix24 SDK ready:', isReady)
	})
}

// ============================================================================
// DEVELOPMENT
// ============================================================================

if (process.env.NODE_ENV === 'development') {
	app.config.performance = true
	console.log('App started in development mode')

	window.__APP_STORE__ = appStore
	window.__ROUTER__ = router
}

// ============================================================================
// Миграции (временные)
// ============================================================================

// import { runMigrations } from '@/utils/migrateModulesToCourse.js'
// runMigrations().catch(console.warn)

// ============================================================================
// Монтирование
// ============================================================================

app.mount('#app')
